import React, { useState, useEffect } from 'react';
import AppIcon from '../../../../components/AppIcon';
import type { IconName } from '../../../../components/AppIcon';
import toast from 'react-hot-toast';

interface Skill {
  name: string;
  timeToLearn: string;
  resources: string[];
  impact: string;
}

interface Category {
  name: string;
  priority: 'High' | 'Medium' | 'Low';
  rationale: string;
  skills: Skill[];
}

interface RecommendationData {
  headline: string;
  futureReadinessScore: number;
  careerProtectionTip: string;
  categories: Category[];
}

interface Employee {
  id: string;
  name: string;
  position: string;
  department: string;
  strengths?: string[];
  growthAreas?: string[];
}

interface LearningRecommendationsPanelProps {
  employee: Employee | null;
  automationPercentage: number | null;
}

const categoryIcons: Record<string, IconName> = {
  'Technical Skills': 'Code2',
  'AI & Automation': 'Cpu',
  'Strategic Thinking': 'Lightbulb',
  'Creative Problem Solving': 'Puzzle',
  'Leadership & Influence': 'Users',
  'Communication': 'MessageSquare',
  'Data & Analytics': 'BarChart3',
  'Adaptability': 'RefreshCw',
};

const categoryColors: Record<string, string> = {
  'Technical Skills': 'from-blue-500 to-cyan-500',
  'AI & Automation': 'from-violet-500 to-purple-600',
  'Strategic Thinking': 'from-amber-500 to-orange-500',
  'Creative Problem Solving': 'from-pink-500 to-rose-500',
  'Leadership & Influence': 'from-emerald-500 to-teal-500',
  'Communication': 'from-sky-500 to-blue-500',
  'Data & Analytics': 'from-indigo-500 to-violet-500',
  'Adaptability': 'from-teal-500 to-emerald-500',
};

const DUMMY_RECOMMENDATIONS: Record<string, RecommendationData> = {
  emp_001: {
    headline: 'Sarah is well-positioned for the future — deepening data-driven design and AI fluency will make her indispensable.',
    futureReadinessScore: 78,
    careerProtectionTip: 'Focus on building expertise where AI assists but cannot replace: complex user empathy, ethical design judgment, and cross-functional influence. Pair your creative instincts with data literacy to become a full-stack design leader.',
    categories: [
      {
        name: 'AI & Automation',
        priority: 'High',
        rationale: 'As AI tools reshape design workflows, Sarah needs to lead AI-assisted design rather than be displaced by it.',
        skills: [
          {
            name: 'AI-Assisted Prototyping (Figma AI, Galileo)',
            timeToLearn: '3-4 weeks',
            resources: ['Figma AI Documentation', 'Galileo AI Course', 'YouTube: AI Design Tools 2025'],
            impact: 'Positions Sarah as the team expert in AI-augmented design, making her 3x faster at prototyping.',
          },
          {
            name: 'Prompt Engineering for Design Briefs',
            timeToLearn: '2-3 weeks',
            resources: ['OpenAI Prompt Guide', 'Coursera: Prompt Engineering', 'Midjourney for Designers'],
            impact: 'Enables rapid concept generation and client communication, a skill few designers currently have.',
          },
        ],
      },
      {
        name: 'Data & Analytics',
        priority: 'High',
        rationale: 'Data-driven design is Sarah\'s top growth area — mastering analytics will elevate her from designer to design strategist.',
        skills: [
          {
            name: 'Product Analytics & A/B Testing',
            timeToLearn: '4-6 weeks',
            resources: ['Mixpanel Academy', 'Optimizely University', 'Google Analytics for UX'],
            impact: 'Allows Sarah to validate design decisions with data, increasing her influence in product roadmap discussions.',
          },
          {
            name: 'SQL for Designers (Basic Querying)',
            timeToLearn: '6-8 weeks',
            resources: ['Mode Analytics SQL Tutorial', 'Khan Academy SQL', 'DataCamp: SQL for Non-Engineers'],
            impact: 'Enables self-service data exploration, reducing dependency on analysts and speeding up research cycles.',
          },
        ],
      },
      {
        name: 'Leadership & Influence',
        priority: 'Medium',
        rationale: 'Sarah\'s next career step requires stakeholder management and design leadership skills beyond individual contribution.',
        skills: [
          {
            name: 'Design Systems Leadership',
            timeToLearn: '8-10 weeks',
            resources: ['Figma Design Systems Course', 'Zeroheight Documentation', 'Brad Frost Atomic Design Book'],
            impact: 'Owning the design system makes Sarah a multiplier for the entire design org, a high-leverage career move.',
          },
          {
            name: 'Executive Storytelling & Presentations',
            timeToLearn: '3-4 weeks',
            resources: ['Duarte Storytelling Course', 'McKinsey Presentation Skills', 'TED Talk Masterclass'],
            impact: 'Enables Sarah to present design strategy to C-suite, opening doors to principal and director roles.',
          },
        ],
      },
      {
        name: 'Strategic Thinking',
        priority: 'Medium',
        rationale: 'Moving from execution to strategy requires understanding business models and product-market fit.',
        skills: [
          {
            name: 'Product Strategy & OKR Frameworks',
            timeToLearn: '4-5 weeks',
            resources: ['Reforge Product Strategy', 'Measure What Matters (Book)', 'Lenny\'s Newsletter'],
            impact: 'Bridges the gap between design craft and business impact, essential for senior IC and leadership tracks.',
          },
        ],
      },
    ],
  },
  emp_002: {
    headline: 'Mike\'s deep technical expertise is a strong foundation — evolving toward AI engineering and product thinking will future-proof his career.',
    futureReadinessScore: 72,
    careerProtectionTip: 'The engineers who thrive in the AI era are those who can architect AI-augmented systems and translate technical complexity into business value. Invest in AI/ML engineering skills and cross-functional communication to stay ahead.',
    categories: [
      {
        name: 'AI & Automation',
        priority: 'High',
        rationale: 'AI is reshaping software engineering — Mike needs to build AI-native systems, not just use AI tools.',
        skills: [
          {
            name: 'LLM Integration & Prompt Engineering',
            timeToLearn: '4-6 weeks',
            resources: ['DeepLearning.AI: LLM Engineering', 'LangChain Documentation', 'OpenAI API Cookbook'],
            impact: 'Enables Mike to build AI-powered features, a skill commanding 30-40% salary premiums in 2025.',
          },
          {
            name: 'MLOps & AI Pipeline Design',
            timeToLearn: '8-10 weeks',
            resources: ['Coursera MLOps Specialization', 'MLflow Documentation', 'Made With ML Course'],
            impact: 'Positions Mike to architect production AI systems, a rare and highly valued engineering skill.',
          },
        ],
      },
      {
        name: 'Leadership & Influence',
        priority: 'High',
        rationale: 'Mike\'s growth areas include mentoring and cross-team communication — critical for Staff Engineer and beyond.',
        skills: [
          {
            name: 'Technical Mentoring & Code Review Culture',
            timeToLearn: '3-4 weeks',
            resources: ['The Staff Engineer\'s Path (Book)', 'Lara Hogan Coaching Guide', 'Google Engineering Practices'],
            impact: 'Multiplies team output and builds Mike\'s reputation as a force multiplier, key for promotion.',
          },
          {
            name: 'Engineering RFC & Design Doc Writing',
            timeToLearn: '2-3 weeks',
            resources: ['Google Design Doc Template', 'Stripe Engineering Blog', 'Increment Magazine'],
            impact: 'Increases Mike\'s influence on architectural decisions across teams beyond his immediate squad.',
          },
        ],
      },
      {
        name: 'Strategic Thinking',
        priority: 'Medium',
        rationale: 'Product thinking is the bridge between Mike\'s technical depth and business impact.',
        skills: [
          {
            name: 'Product Thinking for Engineers',
            timeToLearn: '4-5 weeks',
            resources: ['Reforge Engineering for PMs', 'Shape Up by Basecamp', 'Intercom Product Blog'],
            impact: 'Enables Mike to propose and prioritize features with business context, accelerating his path to Staff/Principal.',
          },
        ],
      },
      {
        name: 'Technical Skills',
        priority: 'Medium',
        rationale: 'Staying current with distributed systems and cloud-native patterns protects Mike\'s technical edge.',
        skills: [
          {
            name: 'Event-Driven Architecture (Kafka, Event Sourcing)',
            timeToLearn: '6-8 weeks',
            resources: ['Confluent Kafka Training', 'Designing Event-Driven Systems (Book)', 'AWS EventBridge Docs'],
            impact: 'Expands Mike\'s architectural toolkit for high-scale systems, a critical skill for senior roles.',
          },
        ],
      },
    ],
  },
  emp_003: {
    headline: 'Lisa\'s analytical foundation is strong — adding machine learning and business storytelling skills will transform her from analyst to strategic data leader.',
    futureReadinessScore: 69,
    careerProtectionTip: 'The data professionals who are hardest to replace are those who combine technical depth with business acumen and communication skills. Invest in ML fundamentals and executive storytelling to move from reporting to advising.',
    categories: [
      {
        name: 'AI & Automation',
        priority: 'High',
        rationale: 'Machine learning is becoming table stakes for senior data roles — Lisa needs ML fluency to stay competitive.',
        skills: [
          {
            name: 'Machine Learning Fundamentals (Scikit-learn)',
            timeToLearn: '8-10 weeks',
            resources: ['Fast.ai Practical ML', 'Kaggle Learn ML', 'Hands-On ML with Scikit-Learn (Book)'],
            impact: 'Opens doors to Data Scientist roles and enables Lisa to build predictive models, not just descriptive reports.',
          },
          {
            name: 'AI-Assisted Analytics (ChatGPT for Data, Julius AI)',
            timeToLearn: '2-3 weeks',
            resources: ['Julius AI Documentation', 'ChatGPT Data Analysis Guide', 'Towards Data Science Blog'],
            impact: 'Dramatically speeds up exploratory analysis and enables Lisa to handle 2x the analytical workload.',
          },
        ],
      },
      {
        name: 'Strategic Thinking',
        priority: 'High',
        rationale: 'Business storytelling is Lisa\'s top growth area — it\'s the skill that separates analysts from strategic advisors.',
        skills: [
          {
            name: 'Data Storytelling & Executive Communication',
            timeToLearn: '4-5 weeks',
            resources: ['Storytelling with Data (Book)', 'Cole Nussbaumer Knaflic Course', 'HBR Data Visualization Guide'],
            impact: 'Transforms Lisa from a report producer to a strategic advisor, dramatically increasing her organizational influence.',
          },
          {
            name: 'Business Strategy & Financial Modeling',
            timeToLearn: '6-8 weeks',
            resources: ['Coursera Business Strategy', 'CFI Financial Modeling', 'McKinsey Insights Newsletter'],
            impact: 'Enables Lisa to connect data insights to P&L impact, making her indispensable to leadership decisions.',
          },
        ],
      },
      {
        name: 'Technical Skills',
        priority: 'Medium',
        rationale: 'Advanced SQL and Python skills will automate Lisa\'s routine work and free her for higher-value analysis.',
        skills: [
          {
            name: 'Advanced Python for Data Analysis (Pandas, Polars)',
            timeToLearn: '6-8 weeks',
            resources: ['Python for Data Analysis (Book)', 'Real Python Tutorials', 'Polars Documentation'],
            impact: 'Enables Lisa to handle datasets 10x larger and build reusable analytical pipelines.',
          },
          {
            name: 'dbt (Data Build Tool) for Analytics Engineering',
            timeToLearn: '4-5 weeks',
            resources: ['dbt Learn Course', 'Analytics Engineering Guide', 'dbt Community Slack'],
            impact: 'Bridges the gap between data engineering and analysis, making Lisa a full-stack analytics professional.',
          },
        ],
      },
    ],
  },
  emp_004: {
    headline: 'Robert\'s marketing leadership is solid — mastering AI-driven marketing and data analytics will make him a next-generation CMO candidate.',
    futureReadinessScore: 74,
    careerProtectionTip: 'The marketers who thrive in the AI era combine creative strategy with data fluency and AI tool mastery. Double down on brand strategy and customer psychology — areas where human judgment is irreplaceable — while building AI and analytics skills.',
    categories: [
      {
        name: 'AI & Automation',
        priority: 'High',
        rationale: 'AI is transforming every marketing function — Robert needs to lead AI adoption, not just use AI tools.',
        skills: [
          {
            name: 'AI Content Strategy & Personalization at Scale',
            timeToLearn: '4-5 weeks',
            resources: ['HubSpot AI Marketing Course', 'Jasper AI Masterclass', 'MarTech Alliance AI Guide'],
            impact: 'Enables Robert to run hyper-personalized campaigns at scale, delivering 3-5x better conversion rates.',
          },
          {
            name: 'Marketing Automation Architecture (HubSpot, Marketo)',
            timeToLearn: '6-8 weeks',
            resources: ['HubSpot Academy', 'Marketo University', 'Salesforce Trailhead Marketing'],
            impact: 'Positions Robert to design full-funnel automation systems, a critical skill for VP and CMO roles.',
          },
        ],
      },
      {
        name: 'Data & Analytics',
        priority: 'High',
        rationale: 'Data-driven marketing is Robert\'s growth area — analytics fluency will elevate him from manager to strategist.',
        skills: [
          {
            name: 'Marketing Mix Modeling & Attribution',
            timeToLearn: '6-8 weeks',
            resources: ['Google Meridian MMM', 'Coursera Marketing Analytics', 'Nielsen Marketing Analytics'],
            impact: 'Enables Robert to prove marketing ROI with statistical rigor, essential for budget justification to CFOs.',
          },
          {
            name: 'Customer Data Platforms (CDP) & Segmentation',
            timeToLearn: '4-5 weeks',
            resources: ['Segment CDP Academy', 'Twilio CDP Documentation', 'CDP Institute Resources'],
            impact: 'Unlocks advanced audience segmentation capabilities, improving campaign targeting precision by 60%.',
          },
        ],
      },
      {
        name: 'Strategic Thinking',
        priority: 'Medium',
        rationale: 'Brand strategy and go-to-market planning are Robert\'s path to executive leadership.',
        skills: [
          {
            name: 'Brand Strategy & Positioning Frameworks',
            timeToLearn: '4-6 weeks',
            resources: ['April Dunford Obviously Awesome (Book)', 'Brand Strategy Course by CXL', 'Harvard Brand Management'],
            impact: 'Builds Robert\'s strategic brand leadership credentials, essential for CMO and VP Marketing roles.',
          },
        ],
      },
      {
        name: 'Leadership & Influence',
        priority: 'Medium',
        rationale: 'Cross-functional leadership and executive communication are key differentiators for senior marketing roles.',
        skills: [
          {
            name: 'Executive Stakeholder Management & Board Reporting',
            timeToLearn: '3-4 weeks',
            resources: ['McKinsey Executive Communication', 'Duarte Resonate (Book)', 'HBR Leadership Essentials'],
            impact: 'Prepares Robert to present marketing strategy at board level, a prerequisite for C-suite advancement.',
          },
        ],
      },
    ],
  },
};

const DEFAULT_RECOMMENDATIONS: RecommendationData = {
  headline: 'This employee has strong foundational skills — targeted upskilling in AI fluency and strategic thinking will future-proof their career.',
  futureReadinessScore: 71,
  careerProtectionTip: 'Focus on developing skills that AI cannot easily replicate: complex judgment, relationship building, creative problem-solving, and strategic thinking. Pair these with AI tool fluency to become a force multiplier in your team.',
  categories: [
    {
      name: 'AI & Automation',
      priority: 'High',
      rationale: 'Understanding and leveraging AI tools in your domain will be essential for staying competitive in the next 3-5 years.',
      skills: [
        {
          name: 'AI Tool Fluency for Your Role',
          timeToLearn: '3-4 weeks',
          resources: ['Coursera AI for Everyone', 'LinkedIn Learning AI Essentials', 'Google AI Fundamentals'],
          impact: 'Positions you as an AI-augmented professional, increasing your output and value to the organization.',
        },
        {
          name: 'Prompt Engineering Fundamentals',
          timeToLearn: '2-3 weeks',
          resources: ['OpenAI Prompt Engineering Guide', 'DeepLearning.AI Short Courses', 'Anthropic Prompt Library'],
          impact: 'Enables effective use of AI assistants for your daily work, saving hours each week.',
        },
      ],
    },
    {
      name: 'Strategic Thinking',
      priority: 'High',
      rationale: 'Strategic thinking is one of the hardest skills to automate and is highly valued at every career level.',
      skills: [
        {
          name: 'Systems Thinking & Problem Framing',
          timeToLearn: '4-6 weeks',
          resources: ['Thinking in Systems (Book)', 'IDEO Design Thinking Course', 'MIT OpenCourseWare Systems'],
          impact: 'Enables you to tackle complex, ambiguous problems — a skill that commands premium compensation.',
        },
      ],
    },
    {
      name: 'Leadership & Influence',
      priority: 'Medium',
      rationale: 'Influence and leadership skills amplify your impact beyond individual contribution.',
      skills: [
        {
          name: 'Stakeholder Communication & Influence',
          timeToLearn: '3-4 weeks',
          resources: ['Crucial Conversations (Book)', 'Coursera Influencing Skills', 'HBR Communication Guide'],
          impact: 'Increases your organizational influence and opens doors to leadership opportunities.',
        },
      ],
    },
  ],
};

const LearningRecommendationsPanel: React.FC<LearningRecommendationsPanelProps> = ({ employee, automationPercentage }) => {
  const [recommendations, setRecommendations] = useState<RecommendationData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  // Load dummy data when employee changes
  useEffect(() => {
    if (employee) {
      const data = DUMMY_RECOMMENDATIONS[employee.id] || DEFAULT_RECOMMENDATIONS;
      setRecommendations(data);
      setActiveCategory(data.categories?.[0]?.name || null);
    } else {
      setRecommendations(null);
      setActiveCategory(null);
    }
  }, [employee?.id]);

  const handleGenerate = () => {
    if (!employee) return;
    setIsGenerating(true);
    setRecommendations(null);

    // Simulate AI generation with mock data
    setTimeout(() => {
      const data = DUMMY_RECOMMENDATIONS[employee.id] || DEFAULT_RECOMMENDATIONS;
      setRecommendations(data);
      setActiveCategory(data.categories?.[0]?.name || null);
      setIsGenerating(false);
      toast.success('Learning pathways generated');
    }, 1500);
  };

  const priorityColor: Record<string, string> = {
    High: 'bg-rose-100 text-rose-700',
    Medium: 'bg-amber-100 text-amber-700',
    Low: 'bg-emerald-100 text-emerald-700',
  };

  const activeData = recommendations?.categories?.find((c) => c.name === activeCategory);

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
            <AppIcon name="GraduationCap" size={18} color="white" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-base">Learning & Growth Pathways</h3>
            <p className="text-xs text-muted-foreground">AI-generated development recommendations</p>
          </div>
        </div>
        <button
          onClick={handleGenerate}
          disabled={!employee || isGenerating}
          className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-medium px-4 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <AppIcon name="Sparkles" size={14} />
              Regenerate with AI
            </>
          )}
        </button>
      </div>

      {!employee && (
        <div className="text-center py-10 text-muted-foreground">
          <AppIcon name="UserSearch" size={36} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">Select an employee to generate learning pathways</p>
        </div>
      )}

      {isGenerating && (
        <div className="text-center py-10">
          <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3 animate-pulse">
            <AppIcon name="GraduationCap" size={28} color="#10b981" />
          </div>
          <p className="text-sm font-medium text-foreground mb-1">Building your learning pathway...</p>
          <p className="text-xs text-muted-foreground">Personalizing recommendations based on role and automation trends</p>
        </div>
      )}

      {recommendations && !isGenerating && (
        <div className="space-y-4">
          {/* Headline & Score */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">{recommendations.headline}</p>
              <p className="text-xs text-muted-foreground mt-1">{recommendations.careerProtectionTip}</p>
            </div>
            <div className="text-center flex-shrink-0">
              <div className="text-3xl font-bold text-emerald-600">{recommendations.futureReadinessScore}</div>
              <div className="text-xs text-muted-foreground">Future Ready Score</div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2">
            {recommendations.categories?.map((cat) => (
              <button
                key={cat.name}
                onClick={() => setActiveCategory(cat.name)}
                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-all ${
                  activeCategory === cat.name
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                <AppIcon name={categoryIcons[cat.name] || 'BookOpen'} size={11} />
                {cat.name}
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeCategory === cat.name ? 'bg-white/20 text-white' : priorityColor[cat.priority]
                }`}>
                  {cat.priority}
                </span>
              </button>
            ))}
          </div>

          {/* Active Category Detail */}
          {activeData && (
            <div className="border border-border rounded-xl overflow-hidden">
              <div className={`bg-gradient-to-r ${categoryColors[activeData.name] || 'from-gray-500 to-gray-600'} px-4 py-3`}>
                <div className="flex items-center gap-2">
                  <AppIcon name={categoryIcons[activeData.name] || 'BookOpen'} size={16} color="white" />
                  <span className="text-sm font-semibold text-white">{activeData.name}</span>
                </div>
                <p className="text-xs text-white/80 mt-1">{activeData.rationale}</p>
              </div>
              <div className="p-4 space-y-3">
                {activeData.skills?.map((skill, i) => (
                  <div key={i} className="bg-muted/30 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-foreground">{skill.name}</span>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full flex items-center gap-1">
                        <AppIcon name="Clock" size={10} />
                        {skill.timeToLearn}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{skill.impact}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {skill.resources?.map((r) => (
                        <span key={r} className="text-xs bg-white border border-border text-foreground px-2 py-0.5 rounded-full flex items-center gap-1">
                          <AppIcon name="ExternalLink" size={9} />
                          {r}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LearningRecommendationsPanel;
